import React, { useCallback, useMemo, useState } from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';

import {
  EMPTY_RESPONSE_MESSAGE,
  INTEGRATION_TEST_MODAL_CONSTANTS,
  TAB_TRIGGER_STYLES
} from '../integration-test-modal.constants';
import { httpStatusUtils } from '../integration-test-modal.utils';

import { CodeBlock } from './CodeBlock';
import { IResponsePanelProps, ResponseTab } from './ResponsePanel.types';
import { ResponseStatusBadge } from './ResponseStatusBadge';
import { StackedCard } from './StackedCard';

export const ResponsePanel: React.FC<IResponsePanelProps> = ({
  response,
  className
}) => {
  const [activeTab, setActiveTab] = useState<ResponseTab>(ResponseTab.BODY);

  const handleTabChange = useCallback((value: string): void => {
    setActiveTab(value as ResponseTab);
  }, []);

  const hasHeaders = useMemo(
    () => response && Object.keys(response.responseHeaders).length > 0,
    [response]
  );

  const statusBadge = useMemo(() => {
    if (!response) return null;
    const statusText = httpStatusUtils.getStatusText(
      response.statusCode,
      response.success
    );
    return (
      <ResponseStatusBadge
        statusCode={response.statusCode}
        statusText={statusText}
      />
    );
  }, [response]);

  const titleRightContent = useMemo(() => {
    if (!response) return null;
    return <div className="flex items-center gap-3">{statusBadge}</div>;
  }, [response, statusBadge]);

  const displayBodyContent = useMemo(() => {
    if (!response) {
      return EMPTY_RESPONSE_MESSAGE;
    }
    return response.responseBody ?? { success: response.success };
  }, [response]);

  const displayHeadersContent = useMemo(
    () => response?.responseHeaders ?? {},
    [response]
  );

  if (!response) {
    return (
      <StackedCard title={INTEGRATION_TEST_MODAL_CONSTANTS.RESPONSE_TITLE} className={className}>
        <CodeBlock content={EMPTY_RESPONSE_MESSAGE} className="min-h-[120px]" />
      </StackedCard>
    );
  }

  return (
    <StackedCard
      title={INTEGRATION_TEST_MODAL_CONSTANTS.RESPONSE_TITLE}
      titleRightContent={titleRightContent}
      className={className}
    >
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="bg-transparent border-b border-base-border rounded-none w-full justify-start gap-4 h-auto p-0">
          {hasHeaders && (
            <TabsTrigger
              value={ResponseTab.HEADERS}
              className={TAB_TRIGGER_STYLES}
            >
              {INTEGRATION_TEST_MODAL_CONSTANTS.HEADERS_TAB}
            </TabsTrigger>
          )}
          <TabsTrigger value={ResponseTab.BODY} className={TAB_TRIGGER_STYLES}>
            {INTEGRATION_TEST_MODAL_CONSTANTS.BODY_TAB}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={ResponseTab.BODY} className="mt-4">
          <CodeBlock content={displayBodyContent} className="min-h-[120px]" />
        </TabsContent>

        {hasHeaders && (
          <TabsContent value={ResponseTab.HEADERS} className="mt-4">
            <CodeBlock
              content={displayHeadersContent}
              className="min-h-[120px]"
            />
          </TabsContent>
        )}
      </Tabs>
    </StackedCard>
  );
};
